/* ==========================================================================
   SELLAMAX — theme.js
   Núcleo compartido: utilidades, analítica, menú móvil, carrito lateral,
   barra fija, video ligero y acordeones instrumentados.
   Sin dependencias externas.
   ========================================================================== */

(function () {
  'use strict';

  var Theme = window.SellamaxTheme || {};
  window.SellamaxTheme = Theme;

  /* ------------------------------------------------------------------------
     Utilidades
     ------------------------------------------------------------------------ */

  Theme.formatMoney = function (cents) {
    var format = Theme.moneyFormat || '${{amount}}';
    var value = (cents / 100).toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    var valueNoDecimals = Math.round(cents / 100).toLocaleString('es-MX');

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, valueNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, valueNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, value);
  };

  Theme.announce = function (message) {
    var region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  };

  /* ------------------------------------------------------------------------
     Analítica
     Publica cada evento en tres canales cuando existen: dataLayer (GTM),
     gtag (GA4) y Shopify analytics. Documentado en README.md.
     ------------------------------------------------------------------------ */

  Theme.track = function (eventName, payload) {
    var data = payload || {};

    if (Theme.debug) {
      // Modo diagnóstico activable desde el editor del theme.
      console.log('[sellamax:analytics]', eventName, data);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, data));

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, data);
    }

    document.dispatchEvent(new CustomEvent('sellamax:' + eventName, { detail: data }));
  };

  /* ------------------------------------------------------------------------
     Trampa de foco para paneles (menú y carrito)
     ------------------------------------------------------------------------ */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function handler(event) {
      if (event.key === 'Escape') {
        onEscape();
        return;
      }
      if (event.key !== 'Tab') return;

      var items = Array.prototype.filter.call(
        container.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handler);
    return function () {
      container.removeEventListener('keydown', handler);
    };
  }

  /* ------------------------------------------------------------------------
     Menú móvil
     ------------------------------------------------------------------------ */

  function initMobileMenu() {
    var menu = document.querySelector('[data-mobile-menu]');
    if (!menu || menu.dataset.menuReady === 'true') return;
    menu.dataset.menuReady = 'true';

    var toggles = document.querySelectorAll('[data-mobile-menu-toggle]');
    var closeButtons = menu.querySelectorAll('[data-mobile-menu-close]');
    var overlay = menu.querySelector('.mobile-menu__overlay');
    var panel = menu.querySelector('.mobile-menu__panel');
    var opener = null;
    var releaseFocus = null;

    function open(trigger) {
      opener = trigger || null;
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute('aria-expanded', 'true');
      });
      releaseFocus = trapFocus(panel, close);
      var firstLink = panel.querySelector(FOCUSABLE);
      if (firstLink) firstLink.focus();
    }

    function close() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      if (releaseFocus) releaseFocus();
      if (opener) opener.focus();
    }

    Array.prototype.forEach.call(toggles, function (toggle) {
      toggle.addEventListener('click', function () {
        if (menu.classList.contains('is-open')) close();
        else open(toggle);
      });
    });

    Array.prototype.forEach.call(closeButtons, function (button) {
      button.addEventListener('click', close);
    });

    if (overlay) overlay.addEventListener('click', close);

    // Cierra al navegar a un ancla dentro de la misma página.
    Array.prototype.forEach.call(panel.querySelectorAll('a[href^="#"]'), function (link) {
      link.addEventListener('click', close);
    });
  }

  /* ------------------------------------------------------------------------
     Carrito lateral
     ------------------------------------------------------------------------ */

  var CartDrawer = {
    element: null,
    opener: null,
    release: null,

    mount: function () {
      this.element = document.querySelector('[data-cart-drawer]');
      if (!this.element || this.element.dataset.drawerReady === 'true') return;
      this.element.dataset.drawerReady = 'true';

      var self = this;
      var overlay = this.element.querySelector('.cart-drawer__overlay');
      if (overlay) overlay.addEventListener('click', function () { self.close(); });

      Array.prototype.forEach.call(
        this.element.querySelectorAll('[data-cart-drawer-close]'),
        function (button) {
          button.addEventListener('click', function () { self.close(); });
        }
      );

      Array.prototype.forEach.call(
        document.querySelectorAll('[data-cart-drawer-open]'),
        function (button) {
          button.addEventListener('click', function (event) {
            event.preventDefault();
            self.open(button);
          });
        }
      );

      this.element.addEventListener('click', function (event) {
        var changeButton = event.target.closest('[data-cart-change]');
        if (!changeButton) return;
        event.preventDefault();
        self.change(changeButton.dataset.cartChange, changeButton.dataset.cartQuantity);
      });
    },

    open: function (trigger) {
      if (!this.element) return;
      this.opener = trigger || null;
      this.refresh();
      this.element.classList.add('is-open');
      this.element.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var panel = this.element.querySelector('.cart-drawer__panel');
      this.release = trapFocus(panel, this.close.bind(this));
      var closeButton = this.element.querySelector('[data-cart-drawer-close]');
      if (closeButton) closeButton.focus();
    },

    close: function () {
      if (!this.element) return;
      this.element.classList.remove('is-open');
      this.element.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.release) this.release();
      if (this.opener) this.opener.focus();
    },

    refresh: function () {
      var self = this;
      return fetch(Theme.routes.cart + '?section_id=cart-drawer', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-cart-drawer-contents]');
          var current = self.element.querySelector('[data-cart-drawer-contents]');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          self.updateCount(parsed);
        })
        .catch(function () {
          /* Silencioso: el carrito sigue disponible en /cart */
        });
    },

    updateCount: function (parsedDoc) {
      var source = parsedDoc && parsedDoc.querySelector('[data-cart-count]');
      var count = source ? source.textContent.trim() : null;
      if (count === null) return;
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-cart-count]'),
        function (node) {
          node.textContent = count;
          node.hidden = count === '0';
        }
      );
    },

    change: function (line, quantity) {
      var self = this;
      return fetch(Theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
      })
        .then(function (response) { return response.json(); })
        .then(function () {
          self.refresh();
          Theme.announce('Carrito actualizado.');
        });
    },

    addFromForm: function (form, button) {
      var self = this;
      var formData = new FormData(form);
      formData.append('sections', 'cart-drawer');

      if (button) button.classList.add('is-loading');

      return fetch(Theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          if (button) button.classList.remove('is-loading');

          if (!result.ok) {
            var message = result.data.description || result.data.message || 'No pudimos agregar el producto.';
            self.showFormError(form, message);
            return null;
          }

          self.clearFormError(form);
          Theme.track('add_to_cart', {
            variant_id: formData.get('id'),
            quantity: Number(formData.get('quantity') || 1)
          });
          Theme.announce('Producto agregado al carrito.');

          if (self.element) self.open(button);
          else window.location.href = Theme.routes.cart;

          return result.data;
        })
        .catch(function () {
          if (button) button.classList.remove('is-loading');
          self.showFormError(form, 'Ocurrió un problema de conexión. Vuelve a intentarlo.');
          return null;
        });
    },

    showFormError: function (form, message) {
      var target = form.querySelector('[data-form-error]');
      if (!target) return;
      target.textContent = message;
      target.hidden = false;
      Theme.announce(message);
    },

    clearFormError: function (form) {
      var target = form.querySelector('[data-form-error]');
      if (!target) return;
      target.textContent = '';
      target.hidden = true;
    }
  };

  Theme.cart = CartDrawer;

  /* ------------------------------------------------------------------------
     Barra fija móvil de compra
     ------------------------------------------------------------------------ */

  function initStickyBar() {
    var bar = document.querySelector('[data-sticky-bar]');
    if (!bar || bar.dataset.stickyReady === 'true') return;
    bar.dataset.stickyReady = 'true';

    document.body.classList.add('has-sticky-bar');

    // Se observa el bloque de compra real: un elemento con altura, para que el
    // IntersectionObserver tenga algo que medir.
    var sentinel =
      document.querySelector('[data-product-form]') ||
      document.querySelector('[data-sticky-sentinel]');

    if (!sentinel || !('IntersectionObserver' in window)) {
      bar.classList.add('is-visible');
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Se muestra cuando el bloque de compra principal sale de pantalla.
          bar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { rootMargin: '-120px 0px 0px 0px' }
    );

    observer.observe(sentinel);
  }

  /* ------------------------------------------------------------------------
     Video ligero: carga el reproductor sólo tras la interacción
     ------------------------------------------------------------------------ */

  function initLiteVideos() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-video-lite]'),
      function (trigger) {
        if (trigger.dataset.videoReady === 'true') return;
        trigger.dataset.videoReady = 'true';

        trigger.addEventListener('click', function () {
          var wrapper = trigger.parentElement;
          var template = wrapper.querySelector('template[data-video-template]');
          if (!template) return;

          var fragment = template.content.cloneNode(true);
          wrapper.innerHTML = '';
          wrapper.appendChild(fragment);
          wrapper.classList.add('video-embed');
          if (trigger.dataset.videoOrientation === 'vertical') {
            wrapper.classList.add('video-embed--vertical');
          }

          var media = wrapper.querySelector('video');
          if (media) {
            media.focus();
            var playPromise = media.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(function () {
                /* El usuario puede iniciar con los controles nativos. */
              });
            }
          }

          Theme.track('video_play', {
            video_title: trigger.dataset.videoTitle || '',
            section_id: trigger.dataset.sectionId || ''
          });
        });
      }
    );
  }

  /* ------------------------------------------------------------------------
     Acordeones instrumentados (compatibilidad y FAQ)
     ------------------------------------------------------------------------ */

  function initAccordions() {
    Array.prototype.forEach.call(
      document.querySelectorAll('details[data-track-open]'),
      function (details) {
        if (details.dataset.trackReady === 'true') return;
        details.dataset.trackReady = 'true';

        details.addEventListener('toggle', function () {
          if (!details.open) return;
          Theme.track(details.dataset.trackOpen, {
            item: details.dataset.trackLabel || ''
          });
        });
      }
    );
  }

  /* ------------------------------------------------------------------------
     Clics de WhatsApp
     ------------------------------------------------------------------------ */

  function initWhatsAppTracking() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('[data-whatsapp-link]');
      if (!link) return;
      Theme.track('whatsapp_click', { location: link.dataset.whatsappLink || 'general' });
    });
  }

  /* ------------------------------------------------------------------------
     Formularios de agregar al carrito
     ------------------------------------------------------------------------ */

  function initCartForms() {
    Array.prototype.forEach.call(
      document.querySelectorAll('form[data-cart-form]'),
      function (form) {
        if (form.dataset.cartFormReady === 'true') return;
        form.dataset.cartFormReady = 'true';

        form.addEventListener('submit', function (event) {
          event.preventDefault();
          var button = form.querySelector('[data-add-button]');
          CartDrawer.addFromForm(form, button);
        });
      }
    );
  }

  /* ------------------------------------------------------------------------
     Selectores de cantidad
     ------------------------------------------------------------------------ */

  function initQuantityInputs() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-quantity-change]');
      if (!button) return;

      var wrapper = button.closest('.quantity');
      var input = wrapper && wrapper.querySelector('input');
      if (!input) return;

      var step = button.dataset.quantityChange === 'up' ? 1 : -1;
      var min = Number(input.min || 1);
      var next = Math.max(min, (Number(input.value) || min) + step);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------------
     Reseñas: "Ver todas"
     ------------------------------------------------------------------------ */

  function initReviews() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-reviews-toggle]'),
      function (button) {
        if (button.dataset.reviewsReady === 'true') return;
        button.dataset.reviewsReady = 'true';

        button.addEventListener('click', function () {
          var extras = document.querySelectorAll('[data-review-extra]');
          Array.prototype.forEach.call(extras, function (item) {
            item.hidden = false;
          });
          button.hidden = true;
          Theme.announce('Se muestran todas las reseñas.');
          Theme.track('reviews_expand', {});
        });
      }
    );
  }

  /* ------------------------------------------------------------------------
     Medición de CTA
     Publica un evento por cada botón con data-cta-location, sin interferir
     con los eventos nativos de Shopify.
     ------------------------------------------------------------------------ */

  function initCtaTracking() {
    document.addEventListener('click', function (event) {
      var cta = event.target.closest('[data-cta-location]');
      if (!cta) return;

      Theme.track('cta_click', {
        cta_location: cta.dataset.ctaLocation || '',
        cta_action: cta.dataset.ctaAction || '',
        variant_id: cta.dataset.selectedVariant || '',
        product_handle: cta.dataset.productHandle || ''
      });
    });
  }

  /* ------------------------------------------------------------------------
     Inicio
     ------------------------------------------------------------------------ */

  function init() {
    initMobileMenu();
    CartDrawer.mount();
    initStickyBar();
    initLiteVideos();
    initAccordions();
    initWhatsAppTracking();
    initCartForms();
    initQuantityInputs();
    initReviews();
  }

  initCtaTracking();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinicializa tras los cambios del editor del theme.
  document.addEventListener('shopify:section:load', init);
})();
